/**
 * Cover 4 Palms Coverage Rules
 * Defines CB reaction patterns for Cover 4 Palms coverage
 * Cover 4 Palms: CBs play aggressive press-man on vertical routes,
 * safeties play quarters coverage over the top
 */

import { CoverageDefinition, CoverageReaction, Position, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';

/**
 * Starting position for CB1 in Cover 4 Palms coverage
 * CB plays tighter press alignment (closer to LOS)
 */
const CB1_COVER4PALMS_START: Position = { x: 20, y: 75 };

/**
 * Starting position for DB/LB1 in Cover 4 Palms coverage
 */
const DBLB1_COVER4PALMS_START: Position = { x: 30, y: 72 };

/**
 * Starting position for FS in Cover 4 Palms coverage
 * Deep quarter zone responsibility
 */
const FS_COVER4PALMS_START: Position = { x: 35, y: 45 };

/**
 * Starting position for CB2 in Cover 4 Palms coverage (mirrors CB1)
 */
const CB2_COVER4PALMS_START: Position = { x: 80, y: 75 };

/**
 * Starting position for DB/LB2 in Cover 4 Palms coverage (mirrors DB/LB1)
 */
const DBLB2_COVER4PALMS_START: Position = { x: 70, y: 72 };

/**
 * Starting position for SS in Cover 4 Palms coverage (mirrors FS)
 */
const SS_COVER4PALMS_START: Position = { x: 65, y: 45 };

/**
 * Starting position for LB in Cover 4 Palms coverage
 */
const LB_COVER4PALMS_START: Position = { x: 50, y: 70 };

/**
 * Cover 4 Palms CB1 reaction rules
 * 
 * Palms rules for CB1:
 * 1. If WR2 runs short out (5yds or under) → CB1 takes WR2
 * 2. If WR2 doesn't run short out, read WR1:
 *    - WR1 runs 10+ yards (deepOut, deepIn, comeback, go, post, corner) → CB1 matches WR1
 *    - WR1 runs 5 yards or shorter (slant, out) → CB1 lets him go, drops in zone
 */
const cover4PalmsReactions: CoverageReaction[] = [
  // === WR2 short out triggers - CB1 takes WR2 ===
  {
    // CB1 matches WR2 on short out route (Palms rule - highest priority)
    routeTrigger: 'out_wr2',
    wrPositionTrigger: { x: 30, y: 80 },
    cbResponse: [
      { x: 22, y: 72 },  // read WR2 releasing outside
      { x: 20, y: 70 },  // break toward WR2
      { x: 15, y: 70 },  // match WR2 to the flat
    ],
    responseDelay: 0,
  },
  
  // === WR1 short routes (5yds or under) - CB1 lets him go, zone drop ===
  {
    // Slant: ~5 yard route - let WR1 go, drop in zone
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 20, y: 70 },  // backpedal
      { x: 20, y: 55 },  // zone drop
      { x: 20, y: 40 },  // settle in zone
    ],
    responseDelay: 0,
  },
  {
    // Out: 5 yard route - let WR1 go, drop in zone
    routeTrigger: 'out',
    wrPositionTrigger: { x: 20, y: 78 },
    cbResponse: [
      { x: 20, y: 70 },  // backpedal
      { x: 20, y: 55 },  // zone drop
      { x: 20, y: 40 },  // settle in zone
    ],
    responseDelay: 0,
  },
  
  // === WR1 deep routes (10+ yards) - CB1 matches WR1 ===
  {
    // Deep Out: 10+ yard route - match WR1
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 65 },  // backpedal with WR1
      { x: 18, y: 50 },  // run with WR1
      { x: 7, y: 42 },   // break to sideline with WR1
    ],
    responseDelay: 0,
  },
  {
    // Deep In: 10+ yard route - match WR1
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 65 },  // backpedal with WR1
      { x: 22, y: 50 },  // run with WR1
      { x: 58, y: 42 },  // break inside with WR1
    ],
    responseDelay: 0,
  },
  {
    // Comeback: 15+ yard route - match WR1
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 65 },  // backpedal with WR1
      { x: 20, y: 35 },  // run deep with WR1
      { x: 17, y: 42 },  // close when WR1 comes back
    ],
    responseDelay: 0,
  },
  {
    // Go: vertical route - match WR1
    routeTrigger: 'go',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 20, y: 70 },  // run with WR1
      { x: 20, y: 50 },  // stride for stride
      { x: 20, y: 8 },   // stay on hip deep
    ],
    responseDelay: 0,
  },
  {
    // Post: 10+ yard route - match WR1
    routeTrigger: 'post',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 65 },  // backpedal with WR1
      { x: 22, y: 50 },  // run with WR1
      { x: 48, y: 18 },  // break inside on post with WR1
    ],
    responseDelay: 0,
  },
  {
    // Corner: 10+ yard route - match WR1
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 65 },  // backpedal with WR1
      { x: 18, y: 50 },  // run with WR1
      { x: 7, y: 18 },   // break outside on corner with WR1
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - under 5yds, let WR1 go, zone drop
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 20, y: 70 },
      { x: 20, y: 55 },
      { x: 20, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - let WR1 go, zone drop
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 20, y: 78 },
    cbResponse: [
      { x: 20, y: 70 },
      { x: 20, y: 55 },
      { x: 20, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - match WR1, close on turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 65 },
      { x: 20, y: 50 },
      { x: 24, y: 58 },
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB1 reaction rules for Cover 4 Palms
 * Key difference from Cover 4 Match: When WR2 runs short out, CB1 takes him,
 * so DB/LB1 just drops in zone instead of following WR2
 * 
 * RB matching: If RB is on the opposite side (DB/LB2's side), DB/LB1 will match
 * RB on short in-breaking routes (curl) as long as WR1 doesn't run short out
 * 
 * WR1 in-breaking: Since DB/LB1 is not on RB's side, he matches WR1 on in-breaking
 * routes (slant) as long as WR1 doesn't run short out
 */
const cover4PalmsDBLB1Reactions: DBLBReaction[] = [
  {
    // WR1 slant (in-breaking) - DB/LB1 follows WR1 since not on RB's side
    // WR1 slant path: (20,85) -> (20,75) -> (75,40) over 2700ms
    // DB/LB1 starts at (30, 72), needs to trail aggressively
    routeTrigger: 'slant',
    response: [
      { x: 30, y: 68 },  // initial read step
      { x: 35, y: 60 },  // break toward slant
      { x: 52, y: 48 },  // trail WR1
      { x: 70, y: 38 },  // continue trailing to end of route
    ],
  },
  {
    // WR2 slant (in-breaking) - DB/LB1 follows WR2 since not on RB's side
    // WR2 slant path (offset): (30,90) -> (30,80) -> (85,45) over 2700ms
    // DB/LB1 starts at (30, 72), needs to trail aggressively
    routeTrigger: 'slant_wr2',
    response: [
      { x: 32, y: 68 },  // initial read step
      { x: 40, y: 58 },  // break toward slant
      { x: 58, y: 48 },  // trail WR2
      { x: 80, y: 40 },  // continue trailing to end of route
    ],
  },
  {
    // WR2 short out - CB1 takes him, DB/LB1 drops in zone
    routeTrigger: 'out',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    // WR1 short out - DB/LB1 follows WR1 to flat
    routeTrigger: 'out_wr1',
    response: [
      { x: 28, y: 65 },
      { x: 22, y: 68 },
      { x: 8, y: 68 },
    ],
  },
  {
    // RB curl (in-breaking) - DB/LB1 matches RB when RB is on opposite side
    // RB curl ends at (52, 80) - DB/LB1 breaks toward RB
    routeTrigger: 'curl_rb',
    response: [
      { x: 29, y: 65 },  // initial backpedal
      { x: 35, y: 70 },  // read RB breaking in
      { x: 52, y: 80 },  // match RB at curl position
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    // Drag: Shallow cross - WR1 in-breaking, DB/LB1 trails
    routeTrigger: 'drag',
    response: [
      { x: 30, y: 68 },
      { x: 40, y: 62 },
      { x: 60, y: 55 },
    ],
  },
  {
    // Hitch: 5-yard stop - zone drop
    routeTrigger: 'hitch',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    // Curl: 10-yard stop - zone drop
    routeTrigger: 'curl',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
];

/**
 * FS reaction rules for Cover 4 Palms
 * Plays deeper quarter zone, provides over-the-top help
 */
const cover4PalmsFSReactions: FSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 35, y: 25 },
      { x: 35, y: 15 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 35, y: 25 },
      { x: 35, y: 15 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 35, y: 25 },
      { x: 20, y: 35 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 35, y: 28 },
      { x: 50, y: 35 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 30, y: 22 },
      { x: 25, y: 35 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 35, y: 20 },
      { x: 30, y: 8 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 35, y: 35 },
      { x: 45, y: 25 },
      { x: 55, y: 15 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 32, y: 30 },
      { x: 15, y: 12 },
    ],
  },
  {
    routeTrigger: 'drag',
    response: [
      { x: 35, y: 25 },
      { x: 35, y: 15 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 35, y: 25 },
      { x: 35, y: 15 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 32, y: 28 },
      { x: 28, y: 35 },
    ],
  },
];

/**
 * CB2 reaction rules for Cover 4 Palms (mirrors CB1)
 * 
 * Palms rules for CB2:
 * 1. If WR3 runs short out (5yds or under) → CB2 takes WR3
 * 2. If WR3 doesn't run short out, read WR4:
 *    - WR4 runs 10+ yards (deepOut, deepIn, comeback, go, post, corner) → CB2 matches WR4
 *    - WR4 runs 5 yards or shorter (slant, out) → CB2 lets him go, drops in zone
 */
const cover4PalmsCB2Reactions: CB2Reaction[] = [
  // === WR3 short out triggers - CB2 takes WR3 ===
  {
    // CB2 matches WR3 on short out route (Palms rule - highest priority)
    routeTrigger: 'out_wr3',
    wrPositionTrigger: { x: 70, y: 80 },
    cbResponse: [
      { x: 78, y: 72 },  // read WR3 releasing outside
      { x: 80, y: 70 },  // break toward WR3
      { x: 85, y: 70 },  // match WR3 to the flat
    ],
    responseDelay: 0,
  },
  
  // === WR4 short routes (5yds or under) - CB2 lets him go, zone drop ===
  {
    // Slant: ~5 yard route - let WR4 go, drop in zone
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 80, y: 70 },  // backpedal
      { x: 80, y: 55 },  // zone drop
      { x: 80, y: 40 },  // settle in zone
    ],
    responseDelay: 0,
  },
  {
    // Out: 5 yard route - let WR4 go, drop in zone
    routeTrigger: 'out',
    wrPositionTrigger: { x: 80, y: 78 },
    cbResponse: [
      { x: 80, y: 70 },  // backpedal
      { x: 80, y: 55 },  // zone drop
      { x: 80, y: 40 },  // settle in zone
    ],
    responseDelay: 0,
  },
  
  // === WR4 deep routes (10+ yards) - CB2 matches WR4 ===
  {
    // Deep Out: 10+ yard route - match WR4
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 65 },  // backpedal with WR4
      { x: 82, y: 50 },  // run with WR4
      { x: 93, y: 42 },  // break to sideline with WR4
    ],
    responseDelay: 0,
  },
  {
    // Deep In: 10+ yard route - match WR4
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 65 },  // backpedal with WR4
      { x: 78, y: 50 },  // run with WR4
      { x: 42, y: 42 },  // break inside with WR4
    ],
    responseDelay: 0,
  },
  {
    // Comeback: 15+ yard route - match WR4
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 65 },  // backpedal with WR4
      { x: 80, y: 35 },  // run deep with WR4
      { x: 83, y: 42 },  // close when WR4 comes back
    ],
    responseDelay: 0,
  },
  {
    // Go: vertical route - match WR4
    routeTrigger: 'go',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 80, y: 70 },  // run with WR4
      { x: 80, y: 50 },  // stride for stride
      { x: 80, y: 8 },   // stay on hip deep
    ],
    responseDelay: 0,
  },
  {
    // Post: 10+ yard route - match WR4
    routeTrigger: 'post',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 65 },  // backpedal with WR4
      { x: 78, y: 50 },  // run with WR4
      { x: 52, y: 18 },  // break inside on post with WR4
    ],
    responseDelay: 0,
  },
  {
    // Corner: 10+ yard route - match WR4
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 65 },  // backpedal with WR4
      { x: 82, y: 50 },  // run with WR4
      { x: 93, y: 18 },  // break outside on corner with WR4
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - under 5yds, let WR4 go, zone drop
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 80, y: 70 },
      { x: 80, y: 55 },
      { x: 80, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - let WR4 go, zone drop
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 80, y: 78 },
    cbResponse: [
      { x: 80, y: 70 },
      { x: 80, y: 55 },
      { x: 80, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - match WR4, close on turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 65 },
      { x: 80, y: 50 },
      { x: 76, y: 58 },
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB2 reaction rules for Cover 4 Palms (mirrors DB/LB1)
 * Key difference from Cover 4 Match: When WR3 runs short out, CB2 takes him,
 * so DB/LB2 just drops in zone instead of following WR3
 * 
 * RB matching: If RB is on the opposite side (DB/LB1's side), DB/LB2 will match
 * RB on short in-breaking routes (curl) as long as WR4 doesn't run short out
 * 
 * WR4 in-breaking: If RB is on opposite side, DB/LB2 matches WR4 on in-breaking
 * routes (slant) as long as WR4 doesn't run short out
 */
const cover4PalmsDBLB2Reactions: DBLB2Reaction[] = [
  {
    // WR4 slant (in-breaking) - DB/LB2 follows WR4 when not on RB's side
    // Default zone drop - AnimationEngine will override when RB is on left side
    routeTrigger: 'slant',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    // WR4 slant - DB/LB2 follows WR4 (used when RB is on opposite side)
    // WR4 slant path mirrored: (80,85) -> (80,75) -> (30,25) over 2700ms
    // DB/LB2 starts at (70, 72), needs to trail aggressively
    routeTrigger: 'slant_wr4',
    response: [
      { x: 70, y: 68 },  // initial read step
      { x: 65, y: 60 },  // break toward slant
      { x: 50, y: 45 },  // trail WR4
      { x: 35, y: 30 },  // continue trailing to end of route
    ],
  },
  {
    // WR3 short out - CB2 takes him, DB/LB2 drops in zone
    routeTrigger: 'out',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    // WR4 short out - DB/LB2 follows WR4 to flat
    routeTrigger: 'out_wr4',
    response: [
      { x: 72, y: 65 },
      { x: 78, y: 68 },
      { x: 92, y: 68 },
    ],
  },
  {
    // RB curl (in-breaking) - DB/LB2 matches RB when RB is on opposite side (left)
    // Mirrored curl position - DB/LB2 breaks toward RB
    routeTrigger: 'curl_rb',
    response: [
      { x: 71, y: 65 },  // initial backpedal
      { x: 65, y: 70 },  // read RB breaking in
      { x: 48, y: 80 },  // match RB at curl position (mirrored)
    ],
  },
  {
    // RB out - DB/LB2 matches RB when RB is on his side (right) and no WR triggers
    // RB out ends at (85, 77)
    routeTrigger: 'out_rb',
    response: [
      { x: 71, y: 65 },  // initial backpedal
      { x: 75, y: 72 },  // break toward RB in flat
      { x: 85, y: 77 },  // match RB at his final position
    ],
  },
  {
    // RB swing - DB/LB2 matches RB when RB is on his side (right) and no WR triggers
    // RB swing ends at (90, 80)
    routeTrigger: 'swing_rb',
    response: [
      { x: 71, y: 65 },  // initial backpedal
      { x: 78, y: 75 },  // break toward RB in flat
      { x: 90, y: 80 },  // match RB at his final position
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    // Drag: Shallow cross - WR4 in-breaking, DB/LB2 trails (mirrored)
    routeTrigger: 'drag',
    response: [
      { x: 70, y: 68 },
      { x: 60, y: 62 },
      { x: 40, y: 55 },
    ],
  },
  {
    // Hitch: 5-yard stop - zone drop
    routeTrigger: 'hitch',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
  {
    // Curl: 10-yard stop - zone drop
    routeTrigger: 'curl',
    response: [
      { x: 71, y: 65 },
      { x: 72, y: 55 },
      { x: 74, y: 45 },
    ],
  },
];

/**
 * SS reaction rules for Cover 4 Palms (mirrors FS)
 */
const cover4PalmsSSReactions: SSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 65, y: 25 },
      { x: 65, y: 15 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 65, y: 25 },
      { x: 65, y: 15 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 65, y: 25 },
      { x: 80, y: 35 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 65, y: 28 },
      { x: 50, y: 35 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 70, y: 22 },
      { x: 75, y: 35 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 65, y: 20 },
      { x: 70, y: 8 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 65, y: 35 },
      { x: 55, y: 25 },
      { x: 48, y: 15 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 68, y: 30 },
      { x: 85, y: 12 },
    ],
  },
  {
    routeTrigger: 'drag',
    response: [
      { x: 65, y: 25 },
      { x: 65, y: 15 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 65, y: 25 },
      { x: 65, y: 15 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 68, y: 28 },
      { x: 72, y: 35 },
    ],
  },
];

/**
 * LB reaction rules for Cover 4 Palms
 * LB drops in zone, picks up unmatched in-breaking routes
 * Paths trail WRs closely - LB should end up near the WR's final position
 */
const cover4PalmsLBReactions: LBReaction[] = [
  {
    // WR1 slant: (20,85) -> (20,75) -> (75,40)
    // LB starts at (50,70), trails WR1 to end near (75,40)
    routeTrigger: 'slant_wr1',
    response: [
      { x: 45, y: 60 },  // read and break toward WR1
      { x: 55, y: 48 },  // cut across to intercept
      { x: 73, y: 38 },  // trail WR1 to final position
    ],
  },
  {
    // WR2 slant (offset): starts ~(30,90), ends ~(85,45)
    // LB trails WR2 to end near (85,45)
    routeTrigger: 'slant_wr2',
    response: [
      { x: 45, y: 60 },  // read and break toward WR2
      { x: 58, y: 50 },  // cut across to intercept
      { x: 83, y: 43 },  // trail WR2 to final position
    ],
  },
  {
    // WR3 slant (mirrored): starts ~(70,90), ends ~(15,45)
    // LB trails WR3 to end near (15,45)
    routeTrigger: 'slant_wr3',
    response: [
      { x: 55, y: 60 },  // read and break toward WR3
      { x: 42, y: 50 },  // cut across to intercept
      { x: 17, y: 43 },  // trail WR3 to final position
    ],
  },
  {
    // WR4 slant (mirrored): starts ~(80,85), ends ~(25,40)
    // LB trails WR4 to end near (25,40)
    routeTrigger: 'slant_wr4',
    response: [
      { x: 55, y: 60 },  // read and break toward WR4
      { x: 45, y: 48 },  // cut across to intercept
      { x: 25, y: 38 },  // trail WR4 to final position
    ],
  },
  {
    // Zone drop - no in-breaking routes to cover
    routeTrigger: 'zone_drop',
    response: [
      { x: 50, y: 60 },  // initial drop
      { x: 50, y: 50 },  // continue drop
      { x: 50, y: 42 },  // settle in zone
    ],
  },
  {
    // WR1 drag: (20,85) -> (20,80) -> (70,75)
    routeTrigger: 'drag_wr1',
    response: [
      { x: 46, y: 68 },  // read
      { x: 42, y: 72 },  // break toward drag path
      { x: 52, y: 74 },  // close on WR1
      { x: 65, y: 75 },  // trail tight
      { x: 70, y: 75 },  // arrive on WR1
    ],
  },
  {
    // WR2 drag (offset): starts ~(30,90), ends ~(80,80)
    routeTrigger: 'drag_wr2',
    response: [
      { x: 46, y: 68 },  // read
      { x: 44, y: 74 },  // break toward drag path
      { x: 56, y: 77 },  // close on WR2
      { x: 70, y: 79 },  // trail tight
      { x: 78, y: 80 },  // arrive on WR2
    ],
  },
  {
    // WR3 drag (mirrored): (70,85) -> (70,80) -> (20,75)
    routeTrigger: 'drag_wr3',
    response: [
      { x: 52, y: 68 },  // read step
      { x: 55, y: 72 },  // break toward drag path
      { x: 42, y: 76 },  // close on WR3
      { x: 22, y: 80 },  // arrive on WR3
    ],
  },
  {
    // WR4 drag (mirrored): (80,85) -> (80,80) -> (30,75)
    routeTrigger: 'drag_wr4',
    response: [
      { x: 52, y: 68 },  // read step
      { x: 55, y: 74 },  // break toward drag path
      { x: 40, y: 74 },  // close on WR4
      { x: 32, y: 75 },  // arrive on WR4
    ],
  },
];

/**
 * Complete Cover 4 Palms coverage definition
 */
export const cover4PalmsDefinition: CoverageDefinition = {
  id: 'cover4Palms',
  name: 'Cover 4 Palms',
  description: 'Quarters coverage with press-man corners and deep safety help',
  initialPosition: CB1_COVER4PALMS_START,
  reactionRules: cover4PalmsReactions,
  dblb1InitialPosition: DBLB1_COVER4PALMS_START,
  dblb1ReactionRules: cover4PalmsDBLB1Reactions,
  fsInitialPosition: FS_COVER4PALMS_START,
  fsReactionRules: cover4PalmsFSReactions,
  cb2InitialPosition: CB2_COVER4PALMS_START,
  cb2ReactionRules: cover4PalmsCB2Reactions,
  dblb2InitialPosition: DBLB2_COVER4PALMS_START,
  dblb2ReactionRules: cover4PalmsDBLB2Reactions,
  ssInitialPosition: SS_COVER4PALMS_START,
  ssReactionRules: cover4PalmsSSReactions,
  lbInitialPosition: LB_COVER4PALMS_START,
  lbReactionRules: cover4PalmsLBReactions,
};

export function getCover4PalmsStartPosition(): Position {
  return { ...CB1_COVER4PALMS_START };
}

export function getCover4PalmsDefinition(): CoverageDefinition {
  return { ...cover4PalmsDefinition };
}
